import * as _ from 'lodash';
import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getGroupVersionKindForModel } from '@console/dynamic-plugin-sdk/src/utils/k8s/k8s-ref';
import { K8sModel } from '@console/dynamic-plugin-sdk/src/api/common-types';
import { k8sPatchResource } from '@console/dynamic-plugin-sdk/src/utils/k8s/k8s-resource';
import { K8sResourceCommon } from '@console/dynamic-plugin-sdk/src/extensions/console-types';
import { OverlayComponent } from '@console/dynamic-plugin-sdk/src/app/modal-support/OverlayProvider';
import { K8sResourceKind } from '../../module/k8s';
import { usePromiseHandler } from '@console/shared/src/hooks/promise-handler';
import {
  ModalBody,
  ModalComponentProps,
  ModalSubmitFooter,
  ModalTitle,
  ModalWrapper,
} from '../factory/modal';
import { ResourceIcon } from '../utils/resource-icon';
import { SelectorInput } from '../utils/selector-input';
import { useK8sWatchResource } from '../utils/k8s-watch-hook';
import { Grid, GridItem } from '@patternfly/react-core';

const LABELS_PATH = '/metadata/labels';
const TEMPLATE_SELECTOR_PATH = '/spec/template/metadata/labels';

const BaseLabelsModal: FC<BaseLabelsModalProps> = ({
  cancel,
  close,
  descriptionKey,
  isPodSelector,
  kind,
  labelClassName,
  messageKey,
  messageVariables,
  path,
  resource,
}) => {
  const [handlePromise, , errorMessage] = usePromiseHandler<K8sResourceCommon>();
  const [labels, setLabels] = useState(
    SelectorInput.arrayify(_.get(resource, path.split('/').slice(1))),
  );
  const [watchedResource, watchedResourceLoaded] = useK8sWatchResource<K8sResourceCommon>({
    kind: resource?.kind,
    name: resource?.metadata?.name,
    namespace: resource?.metadata?.namespace,
  });
  const [stale, setStale] = useState(false);
  const [isInputValid, setIsInputValid] = useState(true);
  const createPath = !labels.length;
  const { t } = useTranslation();

  useEffect(() => {
    if (watchedResourceLoaded && !_.isEmpty(watchedResource)) {
      setStale(!_.isEqual(resource?.metadata?.labels, watchedResource?.metadata?.labels));
    }
  }, [path, resource, watchedResource, watchedResourceLoaded]);

  const submit = useCallback(
    (e): void => {
      e.preventDefault();
      const data = [
        {
          op: createPath ? 'add' : 'replace',
          path,
          value: SelectorInput.objectify(labels),
        },
      ];

      // https://kubernetes.io/docs/user-guide/deployments/#selector
      //   .spec.selector must match .spec.template.metadata.labels, or it will be rejected by the API
      const updateTemplate =
        isPodSelector && !_.isEmpty(_.get(resource, TEMPLATE_SELECTOR_PATH.split('/').slice(1)));

      if (updateTemplate) {
        data.push({
          path: TEMPLATE_SELECTOR_PATH,
          op: 'replace',
          value: SelectorInput.objectify(labels),
        });
      }
      const promise = k8sPatchResource({ model: kind, resource, data });
      handlePromise(promise)
        .then(close)
        .catch(() => {});
    },
    [createPath, path, labels, isPodSelector, resource, kind, handlePromise, close],
  );

  return (
    <form onSubmit={submit} name="form" className="modal-content">
      <ModalTitle>
        {descriptionKey
          ? t('public~Edit {{description}}', {
              description: t(descriptionKey),
            })
          : t('public~Edit labels')}
      </ModalTitle>
      <ModalBody>
        <Grid hasGutter>
          <GridItem>
            {messageKey
              ? t(messageKey, messageVariables)
              : t(
                  'public~Labels help you organize and select resources. Adding labels below will let you query for objects that have similar, overlapping or dissimilar labels.',
                )}
          </GridItem>
          <GridItem>
            <label htmlFor="tags-input">
              {descriptionKey
                ? t('{{description}} for', { description: t(descriptionKey) })
                : t('public~Labels for')}{' '}
              <ResourceIcon groupVersionKind={getGroupVersionKindForModel(kind)} />{' '}
              {resource?.metadata?.name}
            </label>
            <SelectorInput
              onChange={(l) => setLabels(l)}
              onValidationChange={setIsInputValid}
              tags={labels}
              labelClassName={labelClassName || `co-m-${kind.id}`}
              autoFocus
            />
          </GridItem>
        </Grid>
      </ModalBody>
      <ModalSubmitFooter
        errorMessage={errorMessage}
        message={
          stale
            ? t('public~Labels have been updated. Click Cancel and reapply your changes.')
            : undefined
        }
        inProgress={false}
        submitText={t('public~Save')}
        submitDisabled={stale || !isInputValid}
        cancel={cancel}
      />
    </form>
  );
};

export const LabelsModal: FC<LabelsModalProps> = (props) => (
  <BaseLabelsModal path={LABELS_PATH} {...props} />
);

export const LabelsModalOverlay: OverlayComponent<LabelsModalProps> = (props) => (
  <ModalWrapper blocking onClose={props.closeOverlay}>
    <LabelsModal {...props} cancel={props.closeOverlay} close={props.closeOverlay} />
  </ModalWrapper>
);

type BaseLabelsModalProps = {
  descriptionKey?: string;
  isPodSelector?: boolean;
  kind: K8sModel;
  labelClassName?: string;
  messageKey?: string;
  messageVariables?: { [key: string]: string };
  path: string;
  resource: K8sResourceKind;
} & ModalComponentProps;
export type LabelsModalProps = Omit<BaseLabelsModalProps, 'path'>;
